import React from 'react'
import classnames from 'classnames/bind'
import {
  CardContainer,
  InitialsAvatar,
  LabelValue,
  InfoPill,
  PopupTooltip,
  constants,
  utils as coreUtils,
} from '@adc/ui-core'
import * as utils from '../../../utils'
import { ClosingOverviewProps, AssociatedParty } from '../types'
import { getInitials, mapClosingMethodData } from './helpers'
import styles from './styles.scss'

const { formatEnum } = coreUtils.formatters
const { ColorNames } = constants
const {
  date: { formatDateApiToUi },
} = utils

const cx = classnames.bind(styles)

const ELM_ID_PREFIX = 'closing-overview-'

const goToUserManagementEditPage = (party: AssociatedParty) => {
  const url = `/user-management/${party.id}/edit`
  window.open(url, '_blank', 'noopener,noreferrer')
}

const ClosingOverview: React.FC<ClosingOverviewProps> = ({
  closingMethod,
  slots,
  onPartyClick = goToUserManagementEditPage,
  dropShadow = 'medium',
}) => {
  const data = mapClosingMethodData(closingMethod)
  const {
    address,
    debugInfo,
    soldOn,
    soldAmount,
    associatedParties,
    closingStatus,
  } = data

  const formattedClosingStatus = formatEnum(closingStatus)

  return (
    <CardContainer
      className={cx('closing-overview-card')}
      padding='medium'
      borderRadius='medium'
      dropShadow={dropShadow}
    >
      {slots?.leadingActions && (
        <div className={cx('section', 'leading-actions')}>
          {slots.leadingActions(closingMethod)}
        </div>
      )}
      <div
        className={cx('section', 'address', 'address-section')}
        title={`__typename: ${debugInfo?.__typename}, offerOrTrusteeId: ${debugInfo?.offerOrTrusteeId}, Listing ID: ${debugInfo?.listingId}`}
      >
        {Array.isArray(address) ? (
          address.map((line, index) => (
            <div
              key={index}
              className={cx('address-line')}
              data-elm-id={`${ELM_ID_PREFIX}address-${index}`}
            >
              {line}
            </div>
          ))
        ) : (
          <div
            className={cx('address-line')}
            data-elm-id={`${ELM_ID_PREFIX}address-line`}
          >
            {address}
          </div>
        )}
      </div>

      <div className={cx('section')}>
        <LabelValue
          label='Sold On'
          elmId={`${ELM_ID_PREFIX}sold-on`}
          value={formatDateApiToUi(soldOn)}
        />
      </div>

      <div className={cx('section')}>
        <LabelValue
          label='Sold Amount'
          elmId={`${ELM_ID_PREFIX}sold-amount`}
          value={soldAmount ?? 'N/A'}
          isMoneyValue
        />
      </div>

      <div className={cx('section')}>
        <div className={cx('closing-status-wrapper')}>
          <h4>Closing Status</h4>
          {/* @ts-ignore */}
          <PopupTooltip
            trigger={
              <div className={cx('closing-status-trigger')}>
                <InfoPill
                  content={formattedClosingStatus}
                  backgroundColor={ColorNames.CautionLight}
                  foregroundColor={ColorNames.Caution}
                  size='medium'
                  elmId={`${ELM_ID_PREFIX}closing-status`}
                  contentClassName={cx('closing-status-content')}
                  className={cx('closing-status-pill')}
                />
              </div>
            }
            content={formattedClosingStatus}
            position='top center'
            offset={0}
          />
        </div>
      </div>

      {associatedParties.map(party => (
        // @ts-ignore next
        <div className={cx('section', 'parties')} key={party.id}>
          <InitialsAvatar
            // @ts-ignore next
            initials={getInitials(party.name)}
            size='small'
            // @ts-ignore next
            identifier={party.role}
            onClick={() => onPartyClick(party)}
          />
          <LabelValue
            label={party.displayLabel}
            elmId={`${ELM_ID_PREFIX}party-${party.role}`}
            value={party.name}
          />
        </div>
      ))}

      {slots?.trailingActions && (
        <div className={cx('section', 'trailing-actions')}>
          {slots.trailingActions(closingMethod)}
        </div>
      )}
    </CardContainer>
  )
}

export default ClosingOverview
